'use client';

import { DropdownMenu, Spinner } from '@common/components';
import { assert, cn } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { UnauthInfiniteQueryOptions } from '@/api';
import { GiftListItem } from '@/app/_components/DonationTemp/GiftListItem';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrollTop } from '@/hooks/useScrollTop';

import {
  INITIAL_PAGE,
  INITIAL_SIZE,
  PRODUCTS_PER_BLOCK,
  REVIEWS_PER_BLOCK,
} from '../../constants';
import type {
  GiftProductSortType,
  InstitutionOption,
  SortOption,
} from '../../types';

interface ContentsProps {
  institutionOptions: InstitutionOption[];
  sortOptions: SortOption[];
  sort?: GiftProductSortType;
  institutionId?: number;
  categoryId?: number;
  isStockOnly?: boolean;
}

export const Contents = ({
  institutionOptions,
  sortOptions,
  sort,
  institutionId,
  categoryId,
  isStockOnly,
}: ContentsProps) => {
  useScrollTop();
  const router = useCustomRouter();
  const isTablet = useMediaQuery(BREAKPOINTS.TABLET);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);
  const { ref, inView } = useInView();

  const productsPerBlock = (() => {
    if (isTablet) return PRODUCTS_PER_BLOCK.TABLET;
    if (isMobile) return PRODUCTS_PER_BLOCK.MOBILE;

    return PRODUCTS_PER_BLOCK.DESKTOP;
  })();

  const reviewsPerBlock = (() => {
    if (isTablet) return REVIEWS_PER_BLOCK.TABLET;
    if (isMobile) return REVIEWS_PER_BLOCK.MOBILE;

    return REVIEWS_PER_BLOCK.DESKTOP;
  })();

  const {
    data: gifts,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
  } = useInfiniteQuery({
    ...UnauthInfiniteQueryOptions[
      'get:/wello/v2/allows/home/gift-product/block'
    ]({
      query: {
        categoryId,
        institutionId,
        stockOnlyYN: isStockOnly,
        giftProductSortType: sort,
        page: INITIAL_PAGE,
        blockSize: INITIAL_SIZE,
        productsPerBlock: productsPerBlock.count,
        reviewsPerBlock: reviewsPerBlock.count,
      },
    }),
    getNextPageParam: (lastPage) => {
      assert(lastPage.context);

      const { hasNext: has_next, page } = lastPage.context;

      if (has_next) return page + 1;
    },
    initialPageParam: INITIAL_PAGE,
  });

  useEffect(() => {
    if (!isFetching && inView && hasNextPage) fetchNextPage();
  }, [inView, isFetching, fetchNextPage, hasNextPage]);

  const handleSortChange = (value: GiftProductSortType) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('sort', value);

    router.replace(
      DONATION_ROUTES.GIFTS.withSearchParams({
        searchParams: Object.fromEntries(searchParams),
      }),
    );
  };

  const handleInstitutionChange = (value: number) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('institutionId', String(value));

    if (value === 0) {
      searchParams.delete('institutionId');
    }

    router.replace(
      DONATION_ROUTES.GIFTS.withSearchParams({
        searchParams: Object.fromEntries(searchParams),
      }),
    );
  };

  const isEmpty =
    !isLoading && (gifts?.pages?.[0]?.context?.totalCount ?? 0) < 1;

  return (
    <section
      className={cn('flex flex-1 flex-col gap-16 pt-34', 'mobile-tablet:pt-16')}
    >
      <header className="flex items-center justify-between">
        <p
          className={cn(
            'font-500 text-Gray-black typo-body3',
            'mobile-tablet:typo-body4',
          )}
        >
          전체 {gifts?.pages?.[0]?.context?.totalCount?.toLocaleString() ?? 0}
          개
        </p>
        <div className="flex items-center gap-8">
          <DropdownMenu
            options={sortOptions}
            value={sort}
            onChange={(value) => {
              handleSortChange(value);
            }}
            type="text"
            placeholder="정렬 기준 선택"
          />
          <DropdownMenu
            options={institutionOptions}
            value={institutionId}
            onChange={(value) => {
              handleInstitutionChange(value);
            }}
            type="text"
            placeholder="지자체 선택"
            className="hidden mobile-tablet:block"
          />
        </div>
      </header>
      {gifts?.pages ? (
        <div className="flex flex-col gap-48">
          {gifts?.pages
            ?.flatMap((page) => page.context?.blocks ?? [])
            ?.map(
              (
                {
                  products,
                  // reviews
                },
                index,
              ) => (
                <section className="flex flex-col gap-48" key={index}>
                  <ul
                    className={cn('grid gap-x-20 gap-y-48')}
                    style={{
                      gridTemplateColumns: `repeat(${productsPerBlock.cols}, 1fr)`,
                    }}
                    key={index}
                  >
                    {!isLoading &&
                      products.map(
                        ({
                          productNumber: product_number,
                          mainImageUrl: main_image_url,
                          productName: product_name,
                          requiredDonationAmount: required_donation_amount,
                          reviewCount: review_count,
                          reviewRating: review_rating,
                          institutionId: institution_id,
                          institutionLogoUrl: institution_logo_url,
                          institutionName: institution_name,
                          newDisplayYN: new_display_yn,
                          adultYN: adult_yn,
                          productSaleStatus: product_sale_status,
                        }) => (
                          <GiftListItem
                            key={product_number}
                            mainImageSizes={`${BREAKPOINTS.MOBILE} 50vw, ${BREAKPOINTS.TABLET} 33vw, 240px`}
                            mainImageUrl={main_image_url}
                            productName={product_name}
                            donationAmount={required_donation_amount}
                            reviewCount={review_count}
                            reviewRating={review_rating}
                            institution={{
                              id: institution_id,
                              logoUrl: institution_logo_url,
                              name: institution_name,
                            }}
                            productNumber={product_number}
                            isNew={new_display_yn}
                            isAdult={adult_yn}
                            isSoldOut={
                              product_sale_status === GIFT_SALE_STATUS.SOLD_OUT
                            }
                            responsiveClassName={{
                              institutionLogo: 'mobile-tablet:size-20',
                              institutionName:
                                'mobile-tablet:typo-caption1 mobile-tablet:font-600',
                              productName:
                                'mobile-tablet:typo-body4 mobile-tablet:font-500',
                              donationAmount:
                                'mobile-tablet:typo-body3 mobile-tablet:font-700',
                              donationAmountText:
                                'mobile-tablet:typo-body4 mobile-tablet:font-600',
                            }}
                          />
                        ),
                      )}
                    {isLoading &&
                      Array.from({ length: reviewsPerBlock.count }).map(
                        (_, index) => <GiftListItem isLoading key={index} />,
                      )}
                  </ul>
                </section>
              ),
            )}
        </div>
      ) : null}
      {hasNextPage ? (
        <div ref={ref} className="w-full py-150 flex-center">
          <Spinner />
        </div>
      ) : (
        <></>
      )}
      {isEmpty ? (
        <section className="py-160 flex-center">
          <p className="text-center font-400 text-Gray-550 typo-body3">
            아직 등록된 답례품이 없어요.
            <br />
            준비 중이니 조금만 기다려주세요.
          </p>
        </section>
      ) : (
        <></>
      )}
    </section>
  );
};
